/// <reference path="../../css.d.ts" />
"use client";
// @ubm/ui/components/input — LE champ texte canonique (lot U1, 05/10/2026).
// Source : UIArc `registry/components/input/input.tsx` @ 7238f98196b147a8d50ce467abcbad310d4d8414 (MIT) — messages animés
// (hauteur ressort, mots qui montent, chiffres qui roulent), peinture du module, mouvement réduit, repris à l'identique.
// Moteur unique (arbitrage Root : canon UIArc par défaut) : l'ancien `primitives/Input` est DÉPLACÉ ici ; `@ubm/ui` Input
// en est la façade (réexport). Le contrôle reste un vrai <input> HTML : ref, name, type, value/defaultValue, FormData, reset,
// événements et attributs transmis ; `aria-invalid` (invalid / error / reçu), `aria-describedby` (reçu de <Field>, puis aide,
// puis erreur). API UIArc ajoutée, compatible : `label`, `description`, `error` FACULTATIFS — sans eux, AUCUN DOM ajouté
// (le <input> seul, comme avant : <Field> garde son libellé, aucun libellé vide ou dupliqué inventé). Classes historiques
// (crochets `ubm-ui-field ubm-ui-input`, utilitaires) conservées ; la peinture effective est celle du module (hors couche).
// Adaptations : types (`label` ReactNode, `entry!`), anneau de focus Foundry visible ajouté au module (voir CSS).

import { forwardRef, useEffect, useId, useRef, useState } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotionConfig as useReducedMotion } from "motion/react";
import { motionTokens } from "../../lib/motion-tokens";
// Les jetons sont fournis par la charte partagée de l’atelier, sans fondation indépendante.
import styles from "./input.module.css";

/** Présentation d'un contrôle de formulaire : `native` ou `apple` — crochets/attributs de compatibilité ; peinture UIArc. */
export type FieldPresentation = "native" | "apple";

export type InputSize = "xs" | "sm" | "md" | "lg" | "xl";
/** Source UIArc/Foundry adapted to the lab's shared chart and five sizes. */
const inputVariants = ({ size = "md" }: { size?: InputSize; presentation?: FieldPresentation } = {}) =>
  `ubm-ui-field ubm-ui-input ubm-ui-input-${size}`;

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  size?: InputSize;
  /** Non-interactive prefix/icon, or a separately labelled native control. */
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Libellé visible (API UIArc). Absent : aucun DOM ajouté (le libellé vient de <Field> ou d'aria-label). */
  label?: ReactNode;
  /** Aide animée sous le champ (API UIArc), reliée par `aria-describedby`. */
  description?: string;
  /** Erreur animée (API UIArc, `role="alert"`) : pose aussi `aria-invalid`. */
  error?: string;
  /** État d'erreur : pose la bordure danger + aria-invalid (l'enveloppe Field le branche aussi). */
  invalid?: boolean;
  /**
   * Crochets de présentation. Absente : suit `body[data-ui-presentation]`. `apple` / `native` : attribut sur le nœud.
   * La peinture est UIArc dans tous les cas.
   */
  presentation?: FieldPresentation;
  /**
   * Largeur NATIVE en caractères (attribut HTML `size` du vrai <input>), distincte de `size` (gabarit UI sm / md / lg).
   * Absente : aucun attribut `size` posé (défaut du navigateur). Lot API Input, 05/10/2026 — additive.
   */
  htmlSize?: InputHTMLAttributes<HTMLInputElement>["size"];
}

/* Digits roll up when a number grows and down when it shrinks; `custom` hands the latest direction to digits already leaving. */
const digit = {
  enter: (direction: number) => ({ opacity: 0, y: `${direction * 0.6}em`, filter: `blur(${motionTokens.blur.subtle}px)` }),
  center: { opacity: 1, y: 0, filter: "blur(0px)" },
  exit: (direction: number) => ({ opacity: 0, y: `${direction * -0.6}em`, filter: `blur(${motionTokens.blur.subtle}px)` }),
};
const isNumber = (word: string) => /^\d[\d.,/:]*%?$/.test(word);

/** A count in the copy, such as "12 characters", rolls only the digits that changed; a gained or lost digit opens or closes its width. */
function RollingNumber({ word }: { word: string }) {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState({ word, direction: 1 });
  if (shown.word !== word) setShown({ word, direction: parseFloat(word.replace(/,/g, "")) < parseFloat(shown.word.replace(/,/g, "")) ? -1 : 1 });
  const characters = word.split("");
  const transition = reduced ? { duration: 0 } : { y: motionTokens.spring.snappy, width: motionTokens.spring.morph, opacity: { duration: motionTokens.duration.fast }, filter: { duration: motionTokens.duration.fast } };
  return <AnimatePresence initial={false}>{characters.map((character, index) => <motion.span key={characters.length - index} className={styles.column} initial={{ width: 0, opacity: 0 }} animate={{ width: "auto", opacity: 1 }} exit={{ width: 0, opacity: 0 }} transition={transition}>
    <AnimatePresence initial={false} mode="popLayout" custom={shown.direction}><motion.span key={character} custom={shown.direction} variants={digit} initial="enter" animate="center" exit="exit" transition={transition}>{character}</motion.span></AnimatePresence>
  </motion.span>)}</AnimatePresence>;
}

/** Changed words rise in and unblur while unchanged words hold still, and numbers roll. Assistive tech reads the plain copy. */
function MotionText({ text }: { text: string }) {
  const reduced = useReducedMotion();
  const words = text.split(" ");
  return <><span className={styles.srOnly}>{text}</span><span className={styles.words} aria-hidden="true"><AnimatePresence initial={false} mode="popLayout">{words.map((word, index) => <motion.span key={`${index}:${isNumber(word) ? "#" : word}`} className={styles.word}
    initial={reduced ? { opacity: 0 } : { opacity: 0, y: "0.35em", filter: `blur(${motionTokens.blur.soft}px)` }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    exit={reduced ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: "-0.35em", filter: `blur(${motionTokens.blur.subtle}px)`, transition: { duration: motionTokens.duration.exit, ease: [...motionTokens.ease.standard] } }}
    transition={reduced ? { duration: motionTokens.duration.instant } : { duration: motionTokens.duration.standard, ease: [...motionTokens.ease.enter] }}>{isNumber(word) ? <RollingNumber word={word} /> : word}{index < words.length - 1 ? " " : null}</motion.span>)}</AnimatePresence></span></>;
}

/** Helper and error copy: the row opens its height on a spring, then the words settle in. */
function FieldMessage({ id, text, className, alert }: { id?: string; text?: string; className: string; alert?: boolean }) {
  return <AnimatePresence initial={false}>{text ? <MessageRow key="message" id={id} text={text} className={className} alert={alert} /> : null}</AnimatePresence>;
}

/** The row tracks the measured copy, so a longer message that wraps opens its next line instead of snapping. */
function MessageRow({ id, text, className, alert }: { id?: string; text: string; className: string; alert?: boolean }) {
  const reduced = useReducedMotion();
  const copyRef = useRef<HTMLSpanElement>(null);
  const [height, setHeight] = useState<number | "auto">("auto");
  useEffect(() => {
    const node = copyRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setHeight(entry!.borderBoxSize?.[0]?.blockSize ?? node.offsetHeight));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  // Reduced motion mounts the row at full height: a zero-duration open would still paint one collapsed frame. The presence starts
  // with initial={false}, so rows present at hydration render the same on server and client either way.
  return <motion.span className={styles.messageSlot} initial={reduced ? false : { height: 0, opacity: 0 }} animate={{ height, opacity: 1 }} exit={{ height: 0, opacity: 0, transition: reduced ? { duration: 0 } : { height: motionTokens.spring.smooth, opacity: { duration: motionTokens.duration.instant } } }} transition={reduced ? { duration: 0 } : { height: motionTokens.spring.smooth, opacity: { duration: motionTokens.duration.fast } }}>
    <motion.span ref={copyRef} id={id} className={className} role={alert ? "alert" : undefined} initial={reduced ? false : { y: "0.35em", filter: `blur(${motionTokens.blur.soft}px)` }} animate={{ y: 0, filter: "blur(0px)" }} transition={{ duration: reduced ? 0 : motionTokens.duration.standard, ease: [...motionTokens.ease.enter] }}><MotionText text={text} /></motion.span>
  </motion.span>;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, description, error, id, className, size = "md", htmlSize, presentation, invalid, leading, trailing, "aria-invalid": ariaInvalid, ...props }, ref,
) {
  const generatedId = useId();
  const wrapped = label != null || description != null || error != null;
  const controlId = id ?? (wrapped ? generatedId : undefined);
  const hintId = description ? `${controlId}-description` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [props["aria-describedby"], hintId, errorId].filter(Boolean).join(" ") || undefined;
  const isInvalid = error ? true : (ariaInvalid ?? (invalid ? true : undefined));
  const adorned = leading != null || trailing != null;
  const control = (
    <input
      {...props}
      id={controlId}
      ref={ref}
      size={htmlSize}
      aria-invalid={isInvalid}
      aria-describedby={describedBy}
      data-ui-presentation={presentation}
      data-ubm-canon=""
      data-size={size}
      className={[inputVariants({ size }), styles.input, className].filter(Boolean).join(" ")}
    />
  );
  const controlWithAdornments = adorned ? <div className={styles.control} data-size={size} data-invalid={isInvalid === true || isInvalid === "true" ? "true" : undefined} data-disabled={props.disabled ? "true" : undefined}>
    {leading != null ? <span className={styles.adornment}>{leading}</span> : null}
    {control}
    {trailing != null ? <span className={styles.adornment}>{trailing}</span> : null}
  </div> : control;
  if (!wrapped) return controlWithAdornments;
  return <div className={styles.field}>
    {label != null ? <label className={styles.label} htmlFor={controlId}>{label}</label> : null}
    {controlWithAdornments}
    <FieldMessage id={hintId} text={description} className={styles.description!} />
    <FieldMessage id={errorId} text={error} className={styles.error!} alert />
  </div>;
});

Input.displayName = "Input";

export { inputVariants };

export default Input;
